/**
 * SuppliersList Component
 *
 * Displays a table of suppliers with CRUD actions.
 * Supports filtering by active/archived status.
 *
 * User Story: US-P6-001 - Inventory Management
 * Step 1: Suppliers Management
 */

import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useSuppliers } from '@/hooks/useSuppliers'
import SupplierDialog from './SupplierDialog'
import { toast } from 'sonner'
import {
  Building2,
  MoreVertical,
  Pencil,
  Archive,
  RotateCcw,
  Plus,
  Mail,
  Phone,
  Clock,
  ExternalLink,
} from 'lucide-react'

/**
 * Format lead time for display
 */
function formatLeadTime(days) {
  if (!days && days !== 0) return '-'
  if (days === 1) return '1 day'
  return `${days} days`
}

export default function SuppliersList() {
  const {
    suppliers,
    isLoading,
    error,
    deleteSupplier,
    restoreSupplier,
    refetch,
  } = useSuppliers()

  const [filter, setFilter] = useState('active')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingSupplier, setEditingSupplier] = useState(null)
  const [confirmDialog, setConfirmDialog] = useState({ open: false, supplier: null, action: null })

  // Filter suppliers based on active/archived status
  const filteredSuppliers = suppliers.filter(s => {
    if (filter === 'active') return s.active === true
    if (filter === 'archived') return s.active === false
    return true
  })

  const handleAddSupplier = () => {
    setEditingSupplier(null)
    setDialogOpen(true)
  }

  const handleEditSupplier = (supplier) => {
    setEditingSupplier(supplier)
    setDialogOpen(true)
  }

  const handleArchiveClick = (supplier) => {
    setConfirmDialog({ open: true, supplier, action: 'archive' })
  }

  const handleRestoreClick = (supplier) => {
    setConfirmDialog({ open: true, supplier, action: 'restore' })
  }

  const handleConfirmAction = async () => {
    const { supplier, action } = confirmDialog
    if (!supplier || !action) return

    try {
      if (action === 'archive') {
        await deleteSupplier(supplier.id)
        toast.success('Supplier archived', {
          description: `${supplier.name} has been archived.`,
        })
      } else if (action === 'restore') {
        await restoreSupplier(supplier.id)
        toast.success('Supplier restored', {
          description: `${supplier.name} has been restored.`,
        })
      }
    } catch (err) {
      toast.error('Operation failed', {
        description: err.message,
      })
    } finally {
      setConfirmDialog({ open: false, supplier: null, action: null })
    }
  }

  const handleDialogClose = () => {
    setDialogOpen(false)
    setEditingSupplier(null)
  }

  const handleDialogSuccess = () => {
    setDialogOpen(false)
    setEditingSupplier(null)
    refetch()
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-40" />
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center text-red-600 py-8">
            <p className="text-sm font-medium">Error loading suppliers</p>
            <p className="text-xs text-slate-500 mt-1">{error}</p>
            <Button variant="outline" size="sm" className="mt-4" onClick={refetch}>
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div className="flex items-center gap-3">
            <Building2 className="h-5 w-5 text-slate-600" />
            <CardTitle className="text-lg font-medium">Suppliers</CardTitle>
          </div>
          <Button variant="create" size="sm" onClick={handleAddSupplier}>
            <Plus className="h-4 w-4 mr-1" />
            Add Supplier
          </Button>
        </CardHeader>
        <CardContent>
          {/* Filter Tabs */}
          <div className="mb-4">
            <Tabs value={filter} onValueChange={setFilter}>
              <TabsList>
                <TabsTrigger value="active">
                  Active ({suppliers.filter(s => s.active).length})
                </TabsTrigger>
                <TabsTrigger value="archived">
                  Archived ({suppliers.filter(s => !s.active).length})
                </TabsTrigger>
                <TabsTrigger value="all">
                  All ({suppliers.length})
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {/* Suppliers Table */}
          {filteredSuppliers.length === 0 ? (
            <div className="text-center text-slate-500 py-12">
              <Building2 className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm font-medium">No suppliers found</p>
              <p className="text-xs text-slate-400 mt-1">
                {filter === 'archived'
                  ? 'No archived suppliers'
                  : 'Add your first supplier to get started'}
              </p>
              {filter === 'active' && (
                <Button variant="outline" size="sm" className="mt-4" onClick={handleAddSupplier}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Supplier
                </Button>
              )}
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead className="text-center">Lead Time</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead className="w-[60px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSuppliers.map((supplier) => (
                    <TableRow key={supplier.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {supplier.name}
                          {supplier.website && (
                            <a
                              href={supplier.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-slate-400 hover:text-slate-600 transition-colors"
                              title="Visit website"
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                            </a>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {supplier.contact_name || '-'}
                      </TableCell>
                      <TableCell>
                        {supplier.email ? (
                          <a
                            href={`mailto:${supplier.email}`}
                            className="text-slate-600 hover:text-slate-900 flex items-center gap-1"
                          >
                            <Mail className="h-3.5 w-3.5" />
                            {supplier.email}
                          </a>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {supplier.phone ? (
                          <span className="text-slate-600 flex items-center gap-1">
                            <Phone className="h-3.5 w-3.5" />
                            {supplier.phone}
                          </span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="text-slate-600 flex items-center justify-center gap-1">
                          <Clock className="h-3.5 w-3.5" />
                          {formatLeadTime(supplier.lead_time_days)}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge
                          variant="outline"
                          className={
                            supplier.active
                              ? 'bg-green-50 text-green-700 border-green-200'
                              : 'bg-slate-50 text-slate-600 border-slate-200'
                          }
                        >
                          {supplier.active ? 'Active' : 'Archived'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEditSupplier(supplier)}>
                              <Pencil className="mr-2 h-4 w-4" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {supplier.active ? (
                              <DropdownMenuItem
                                onClick={() => handleArchiveClick(supplier)}
                                className="text-slate-600"
                              >
                                <Archive className="mr-2 h-4 w-4" />
                                Archive
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem
                                onClick={() => handleRestoreClick(supplier)}
                                className="text-green-600"
                              >
                                <RotateCcw className="mr-2 h-4 w-4" />
                                Restore
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Supplier Dialog */}
      <SupplierDialog
        open={dialogOpen}
        onOpenChange={handleDialogClose}
        supplier={editingSupplier}
        onSuccess={handleDialogSuccess}
      />

      {/* Confirm Dialog for Archive/Restore */}
      <AlertDialog
        open={confirmDialog.open}
        onOpenChange={(open) => !open && setConfirmDialog({ open: false, supplier: null, action: null })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmDialog.action === 'archive' ? 'Archive Supplier?' : 'Restore Supplier?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDialog.action === 'archive'
                ? `This will hide ${confirmDialog.supplier?.name} from the active suppliers list. You can restore it later if needed.`
                : `This will restore ${confirmDialog.supplier?.name} to the active suppliers list.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmAction}
              className={confirmDialog.action === 'archive' ? '' : 'bg-green-600 hover:bg-green-700'}
            >
              {confirmDialog.action === 'archive' ? 'Archive' : 'Restore'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
